(ns frontend.components.field-editor
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.url :as url]
   [frontend.bootstrap.components :as rbs]
   [frontend.components.inputs.footprint :refer [display-string editor viewer]]
   [frontend.dom-utils :as dom-utils]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [reagent.core :as r]
   [ui.dom-globals :as dg]))

(def tools-shown-unmodified
  {:tool-item/debug    {:icon     (fu/cs :fal :fa-debug)
                        :on-click (fn [_e _attribute _local-atom editor-atom _args]
                                    (swap! editor-atom assoc :debug? (not (:debug? @editor-atom))))}
   :tool-item/clear    {:icon     (fu/cs :fal :fa-times :tool-item__clear)
                        :on-click (fn [_e attribute _local-atom _editor-atom _args on-input-blur!
                                       on-input-change!]
                                    (when on-input-change!
                                      (on-input-change! nil attribute))
                                    (when on-input-blur!
                                      (on-input-blur! attribute nil nil identity)))}
   :tool-item/copy     {:icon     (fu/cs :fal :fa-copy)
                        :on-click (fn [e _attribute _local-atom _editor-atom args]
                                    (clipboard/copy-to
                                     (str
                                      (display-string args)
                                      (when (:secondary-view-args args)
                                        (str " " (display-string (:secondary-view-args args))))))
                                    (dg/prevent-default e))}
   :tool-item/open-url {:icon     (fu/cs :fal :fa-external-link)
                        :on-click (fn [e _attribute _local-atom _editor-atom args]
                                    (let [value (cond (= :editor.type/url (:editor/type args))
                                                      (:value args)

                                                      (string? (:value args))
                                                      (:entity-id args)

                                                      :else
                                                      (:value args))
                                          url-value (when (= :editor.type/url (:editor/type args))
                                                      (url/relative-or-protocol-url
                                                       (str value)
                                                       :https))]
                                      (if (= :editor.type/url (:editor/type args))
                                        (when-let [sanitized (fu/sanitize-url url-value)]
                                          (.open js/window sanitized "_blank"))                     ;; open urls
                                        (routing/open-entity value))
                                      (dg/prevent-default e)))}})
(def tools-shown-modified
  {:tool-item/save   {:icon     (fu/cs :fal :fa-save)
                      :on-click (fn [e _attribute _local-atom editor-atom _args]
                                  (when-let [save! (:save! @editor-atom)]
                                    (save!))
                                  (dg/prevent-default e))}
   :tool-item/cancel {:icon     (fu/cs :fal :fa-undo)
                      :on-click (fn [e _attribute local-atom editor-atom args]
                                  (when-let [cancel! (:cancel-edit! @editor-atom)]
                                    (let [{:keys [:validate! :attribute]} args]
                                      (cancel!)
                                      (swap! local-atom dissoc :has-updates? :editor-value)
                                      (when validate!
                                        (let [error-text (validate! attribute
                                                                    (or (:editor-value @local-atom)
                                                                        (:value args)))]
                                          (if error-text
                                            (swap! editor-atom assoc :error error-text)
                                            (swap! editor-atom dissoc :error))))))
                                  (dg/prevent-default e))}})
(def tools-shown-on-hover
  {:tool-item/edit     {:icon     (fu/cs :fal :fa-edit)
                        :on-click (fn [e _attribute _local-atom editor-atom _args]
                                    (when-let [focus! (:set-focusable-element! @editor-atom)]
                                      (focus!))
                                    (dg/prevent-default e))}
   :tool-item/settings {:icon     (fu/cs :fal :fa-cog)
                        :on-click (fn [_e _attribute _local-atom _editor-atom {:keys [open-settings!]}]
                                    (when open-settings!
                                      (open-settings!)))}})

(defn resolve-editor-type
  [view-args]
  (let [editor-type (or (:editor/type view-args)
                        (:editor/type (:field view-args))
                        (->> view-args
                             :field/default-visibility
                             :editor))]
    (if-not editor-type
      (logging/error "Editor type not found : " view-args)
      editor-type)))

(defn render
  [local-atom drop-count editor-atom
   {:keys [id entity-type entity-unique-name value attribute label hide-label? on-save! class-name mandatory?
           drag-options source-options validate! disabled? read-only? info-text error-link tools secondary-view-args full-width?]
    :as   view-args}]
  (when (or (not attribute))
    (logging/error "NO Attribute : " view-args))
  (let [editor-dom-node (:editor-dom-node @editor-atom)
        has-focus? (:has-focus? @editor-atom)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        secondary-attribute (:attribute secondary-view-args)
        field-mandatory? (if (fn? mandatory?)
                           (mandatory? view-args)
                           (boolean mandatory?))
        on-input-focus! (fn [_e]
                          (swap! editor-atom assoc :has-focus? true)
                          true)
        on-input-change!
        (fn [_e edit-value]
          (if (= edit-value value)
            (swap! local-atom dissoc :has-updates?)
            (swap! local-atom assoc :has-updates? true))
          (swap! local-atom assoc :editor-value edit-value)
          (when validate!
            (let [error-text (validate! attribute edit-value)]
              (if error-text
                (do
                  #_(logging/error "Field editor error on-input-change!: " error-text
                                   " << " edit-value)
                  (swap! editor-atom assoc :error error-text))
                (swap! editor-atom dissoc :error))))
          true)
        on-input-blur!
        (fn [blurred-attribute e value-on-blur clear-temp-cb]
          (let [related-target (some-> e .-relatedTarget)
                value-on-blur (if (string? value-on-blur)
                                (string/trim value-on-blur)
                                value-on-blur)
                field-args (if (= blurred-attribute secondary-attribute)
                             secondary-view-args
                             view-args)
                clear? (or (nil? value-on-blur)
                           (and (set? value-on-blur) (empty? value-on-blur))
                           (and (string? value-on-blur)
                                (empty? (string/trim value-on-blur))))]
            (if (and clear?
                     (or (:field/not-clearable? field-args)
                         field-mandatory?))                                                         ;; if value must not be cleared - error msg
              (do
                (swap! editor-atom dissoc :has-focus?)
                (when-not (:error @editor-atom)
                  (swap! editor-atom assoc :error (loc/tr :value-cannot-be-cleared)
                         :remount (inc (:remount @editor-atom)))
                  (deref editor-atom)))
              (do
                (swap! editor-atom dissoc :error)
                (if-not related-target
                  (swap! editor-atom dissoc :has-focus?)
                  (when-not (dom-utils/is-ancestor? related-target editor-dom-node)
                    (swap! editor-atom dissoc :has-focus?)))
                (when (fn? on-save!)
                  (let [error-text (when validate! (validate! blurred-attribute value-on-blur))]
                    ;; timeout for clearing internal state ? or do we handle this outside ?
                    ;; on success
                    ;; must also think about error messages !!!
                    (if error-text
                      (do
                        (logging/error "Validation error: " error-text)
                        (swap! editor-atom assoc :error error-text))
                      (on-save!
                       blurred-attribute
                       value-on-blur
                       (fn [_]
                         (swap! editor-atom dissoc :error)
                         (when clear-temp-cb
                           (swap! local-atom dissoc :has-updates? :editor-value)
                           (clear-temp-cb)))                                                        ;; clear temp state
                       (fn [err]
                         (let [error-msg (:message err)]
                           (logging/error "ERROR saving value : " error-msg " " value-on-blur)
                           (when error-msg
                             (swap! editor-atom assoc :error (str error-msg))))
                         ;; TODO: error handling - do not reset temp state -> or user would lose temp data on error
                         )))))))))

        label (or (and (string? label) label)
                  (and (fn? label) (label view-args))
                  (when-let [a (:attribute view-args)]
                    (loc/field-name a (registry/get-field-schema tenant-schema a))))
        info-text (or (and (string? info-text) info-text)
                      (and (fn? info-text) (info-text view-args)))
        error (:error @editor-atom)
        error-text-secondary (:error-text-secondary secondary-view-args)
        error-text (or error error-text-secondary)
        custom-error? (and (map? error)
                           (some? (:message error)))
        show-error-link? (and (some? (->> error
                                          :error-details
                                          :conflicting-entity-id))
                              error-link)
        {:keys [:draggable? :hide-drag-area?
                :on-drag-start! :on-drag-end! :on-drag-leave :on-drag-enter :on-drag-over]}
        drag-options
        {:keys [:source-icon-fn]} source-options
        draggable? (if (fn? draggable?)
                     (draggable? view-args)
                     (boolean draggable?))
        hide-drag-area? (if (fn? hide-drag-area?)
                          (hide-drag-area? view-args)
                          (boolean hide-drag-area?))
        drag-attribute (:dragging? view-args)
        drop-location (:drop view-args)
        primary-editor-type (resolve-editor-type view-args)
        ;style (if @(rf/subscribe [:current-theme])
        ;        @(rf/subscribe [:card-column-style-dark])
        ;        @(rf/subscribe [:card-column-style]))
        ]
    ;; when changing field-layout from e.g. tesi-financials to tesi-financials-2, the correct viewer
    ;; won't always get used => forcing remount with attribute based key:
    ^{:key (str "field-editor-" attribute "-" (:remount @editor-atom))}
    [:div #_style
     (merge
      {:id id
       :ref (fn [e]
              (when e
                (swap! editor-atom assoc :editor-dom-node e)))}
      {:class-name (fu/cs (str "field-editor-class__"
                               (when (keyword? entity-type)
                                 (name entity-type))
                               "__" (when (keyword? attribute)
                                      (namespace attribute))
                               "_" (when (keyword? attribute)
                                     (name attribute))
                               "_" (when (keyword? primary-editor-type)
                                     (name primary-editor-type)))
                          (when (keyword? attribute)
                            (str (namespace attribute) "_" (name attribute)))
                          (str
                           "entity-name-class__"
                           (string/replace (str entity-unique-name)
                                           #"\s"
                                           ""))

                          "field-editor"
                          (when has-focus? "focused")
                          (when class-name class-name)
                          (when (= drag-attribute attribute)
                            "dragging")
                          (when (and (> drop-count 0)
                                     (not= drag-attribute attribute))
                            (str "dropzone"
                                 (if (= drop-location :above)
                                   " above"
                                   " below"))))
       :draggable  (boolean draggable?)}
      (when draggable?
        {:on-drag-start (fn [e]
                          (aset e "dataTransfer" "effectAllowed" "move")
                          ;;drag data must be set for dragend to fire in firefox:
                          (.. e -dataTransfer (setData "text" "zapflow"))

                          (on-drag-start! attribute)
                          (dg/stop-propagation e))
         :on-drag-end   (fn [me]
                          (dg/prevent-and-stop me)
                          (on-drag-end! attribute))
         :on-drop       (fn [_e])
         :on-drag-leave (fn [oe]
                          (let [target (->> oe
                                            .-target)
                                already-counted (some #{(str target)} (:drag-counted @local-atom))
                                new-count (dec (:drop-count @local-atom))]
                            (swap! local-atom assoc
                                   :drag-counted
                                   (disj (:drag-counted @local-atom) (str target)))
                            (if already-counted
                              (do
                                (swap! local-atom assoc :drop-count new-count))
                              (logging/log "Skip DECREASE :" attribute "->" new-count "-" target))

                            (when (= (:drop-count @local-atom) 0)
                              (on-drag-leave attribute))
                            (dg/prevent-and-stop oe)))
         :on-drag-over  (fn [e]
                          (dg/prevent-default e))
         :on-drag-enter (fn [ee]
                          (let [target (->> ee
                                            .-target)
                                already-counted (some #{(str target)} (:drag-counted @local-atom))
                                new-count (inc (:drop-count @local-atom))]
                            (swap! local-atom assoc
                                   :drag-counted
                                   (conj (:drag-counted @local-atom) (str target)))
                            (if-not already-counted
                              (do
                                (swap! local-atom assoc :drop-count new-count))
                              (do
                                (logging/log "SKIP Increase - " already-counted)))
                            (when (= (:drop-count @local-atom) 1)
                              (on-drag-enter attribute))
                            (dg/prevent-and-stop ee)))}))
     (when-not hide-drag-area?
       [:div
        {:class-name (fu/cs "field-editor__handle"
                            (when draggable? "draggable")
                            (when read-only? "readonly")
                            (when disabled? "disabled"))}
        (when draggable?
          [:i.fal.fa-grip-vertical])])
     [:div.field-editor__content
      {:draggable     false                                                                         ;; Prevent drag start events from content area
       :on-drag-start (fn [e]
                        (dg/prevent-and-stop e))}
      [:div
       {:class "flex justify-between flex-row relative"}
       (when source-icon-fn
         [:div.field-editor__content__header__source
          [source-icon-fn view-args]])
       (when-not hide-label?
         [:div.field-editor__content__header__label
          (fu/overlay-if
           (:field/label-icon-tooltip view-args)
           [rbs/OverlayTrigger
            {:placement     "top"
             :popper-config {:modifiers [{:name    "preventOverflow"
                                          :options {:main-axis false}}]}
             :overlay       (r/as-element
                             [rbs/Tooltip
                              {:class "in modal-tooltip"
                               :style {:white-space "pre-wrap"}
                               :id    (str "editor-tooltip-" (name attribute))}
                              (:field/label-icon-tooltip view-args)])}]

           [:label
            {:title      (str label)
             :class-name (fu/cs "field-editor__content__header__label__text-content")}
            label
            (when (:field/label-icon view-args)
              [:i
               {:class-name (fu/cs :field-editor__content__header__label__icon
                                   :fal
                                   (:field/label-icon view-args))}])])
          (when field-mandatory?
            [:i.fas.fa-asterisk.mandatory])
          [:span.empty-space]])
       [:ul
        {:class "flex flex-row gap-1 absolute right-0 -top-0.5 tools"}
        (doall
         (for [tool tools]
           (let [shown-on-focus? (get tools-shown-modified tool)
                 shown-on-hover? (get tools-shown-on-hover tool)
                 shown-when-no-updates? (get tools-shown-unmodified tool)
                 tool-config (or shown-on-focus? shown-on-hover? shown-when-no-updates?)]
             ;; A: cancel tool not available for not cancellable elements
             ;; B: open link tool available only
             ;; i: url, link and email types support open link
             ;; ii: single entity types that have a dedicated view can be opened with link
             (when
              (and
               (not (and (some #{tool} [:tool-item/cancel :tool-item/save])
                         (or
                          (not (:has-updates? @local-atom))                                         ;; don't show cancel/save when no updates
                          (some #{primary-editor-type}
                                [:editor.type/toggle :editor.type/checkbox :editor.type/radio
                                 :editor.type/financials :editor.type/tesi-financials])
                          disabled?
                          read-only?)))
               (or (not= tool :tool-item/open-url)
                   (some #{primary-editor-type}
                         [:editor.type/url :editor.type/link :editor.type/email])
                   (and (= primary-editor-type :editor.type/select)
                        (some (set (:field/entity-types view-args))
                              [:entity.type/person :entity.type/company :entity.type/oppo])
                        (:value view-args)))
               (or (not= tool :tool-item/debug)
                   (and (some #{primary-editor-type}
                              [:editor.type/select :editor.type/multi-select])
                        (session/sys-admin?)))                                                      ;; only sys-admin can debug selects
               (not (and (some #{tool} [:tool-item/edit])                                           ;; no edit on read-only or disabled fields
                         (or disabled?
                             (some #{primary-editor-type}
                                   [:editor.type/financials :editor.type/tesi-financials
                                    :editor.type/grid
                                    :editor.type/link
                                    :editor.type/select :editor.type/multi-select])
                             read-only?)))
               (not (and (some #{tool} [:tool-item/clear])                                          ;; no clear for empty or non-clearable
                         (or                                                                        ;(not (some #{primary-editor-type} [:editor.type/select :editor.type/multi-select]))
                          (nil? value)
                          read-only?
                          (:field/not-clearable? view-args)
                          (:not-clearable? view-args))))
               (not (and (some #{tool} [:tool-item/copy])                                           ;; no copy for empty values
                         (or (nil? value)
                             (and (string? value) (empty? value))
                             (and (coll? value) (empty? value))))))
               (if (or (and (not (:has-updates? @local-atom)) shown-when-no-updates?)
                       (and has-focus? shown-on-focus?)
                       shown-on-hover?)
                 [:li
                  {:key           (str (:namespace (:attribute view-args))
                                       (:name (:attribute view-args))
                                       tool)
                   :class-name    (fu/cs "field-editor__content__header__tools__item"
                                         (if shown-on-hover? "hover" "focused"))
                   :tab-index     -1
                   :on-mouse-down (fn [e]
                                    ((:on-click tool-config)
                                     e
                                     attribute
                                     local-atom
                                     editor-atom
                                     view-args
                                     on-input-blur!
                                     on-input-change!))}
                  [:i {:class-name (:icon tool-config)}]])))))]]
      [:div.field-editor__content__field-input
       (when read-only?
         [viewer view-args])
       (when (and secondary-attribute secondary-view-args)
         [:div
          {:class-name (fu/cs "field-editor__content__field-input__secondary-content"
                              (when (= (:field/data-type secondary-view-args)
                                       :data.type/currency-symbol)
                                "currency-symbol__container"))}
          (if read-only?
            [viewer secondary-view-args]
            [editor
             (merge (select-keys view-args [:close-menu-on-scroll? :menu-portal-target])
                    secondary-view-args
                    {:editor/type      (resolve-editor-type secondary-view-args)
                     :tenant           tenant
                     :tenant-schema    tenant-schema
                     :state-atom       editor-atom
                     :unique-id        entity-unique-name
                     :max-opts-visible (when (= (:field/data-type secondary-view-args)
                                                :data.type/currency-symbol)
                                         5)
                     :has-focus?       has-focus?
                     :class-name       (fu/cs (when error-text-secondary "error")
                                              (when read-only? "readonly")
                                              (when disabled? "disabled")
                                              "secondary-input")
                     :on-focus!        on-input-focus!
                     :on-blur!         (partial on-input-blur! secondary-attribute)})])])
       (when-not read-only?
         [:div.field-editor__content__field-input__primary-content
          [editor
           (merge
            view-args
            {:editor/type            primary-editor-type
             :tenant                 tenant
             :full-width?            full-width?
             :tenant-schema          @session/tenant-schema
             :state-atom             editor-atom
             :unique-id              entity-unique-name
             :debug?                 (:debug? @editor-atom)
             :has-focus?             has-focus?
             :class-name             (fu/cs (when error-text "error")
                                            (when read-only? "readonly")
                                            (when disabled? "disabled")
                                            (when (and secondary-attribute secondary-view-args)
                                              "primary-input"))
             :set-focusable-element! (fn [e]
                                       (swap! editor-atom assoc :set-focusable-element! e))
             :set-cancel-edit!       (fn [e]
                                       (swap! editor-atom assoc :cancel-edit! e))
             :set-save!              (fn [e]
                                       (swap! editor-atom assoc :save! e))
             :on-change!             on-input-change!
             :on-error!              (fn [error-text]
                                       (swap! editor-atom assoc :error error-text))
             :clear-error!           (fn []
                                       (swap! editor-atom dissoc :error))
             :on-focus!              on-input-focus!
             :on-blur!               (partial on-input-blur! attribute)
             :field/not-clearable?   true})]])]

      [:div.field-editor__content__field-input__primary-content__info
       (when-not read-only?

         (cond
           custom-error?
           [:span.field-editor__content__field-input__primary-content__info-error
            (:message error)
            (when show-error-link?
              (let [conflicting-entity-id (->> error
                                               :error-details
                                               :conflicting-entity-id)]
                [error-link
                 (fn [e]
                   (dg/prevent-and-stop e)
                   (routing/open-entity conflicting-entity-id))
                 (:person/universal-email-address (session/get-entity
                                                   conflicting-entity-id))]))]
           error-text
           [:span.field-editor__content__field-input__primary-content__info-error
            error-text]
           info-text
           [:span.field-editor__content__field-input__primary-content__info-text.info-text
            info-text]))]]]))

(defn view
  [{:keys [attribute value validate! validate-on-start? external-editor-atom]}]
  (let [editor-atom (or external-editor-atom (r/atom {:remount 1}))                                 ;(r/cursor state-atom [attribute])
        local-atom (r/atom {:drop-count   0
                            :drag-counted #{}})]                                                    ;; when do we reset the state ?
    (when validate-on-start?
      (let [error-text (validate! attribute value)]
        (when error-text
          (swap! editor-atom assoc :error error-text))))
    (r/create-class
     {:display-name        "field-editor"
      :component-did-mount (fn [_args]
                             ;; we use this to know where focus is changing - we can prevent blur if focus is changing to
                             ;; events withing this editor if needed
                             )
      :reagent-render      (fn [args]
                             (when (and (not (:dragging? args))
                                        (not= (:drop-count @local-atom) 0))
                               (reset! local-atom {:drop-count   0
                                                   :drag-counted #{}}))                             ;; reset local drop counter if we're not dragging
                             (render local-atom
                                     (:drop-count @local-atom)
                                     editor-atom
                                     args))})))
